import React, { useEffect, useState, useMemo, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Upload, FileText, Download, Trash2, CheckCircle2, XCircle, Folder, Search,
    Plus, X, Clock, ShieldCheck, ClipboardList, MessageSquare, RotateCw, AlertTriangle,
} from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const DOC_CATEGORIES = [
    "Plans", "Specs", "Contracts", "Permits", "Inspections", "Engineering",
    "Invoices", "Receipts", "Insurance", "LienWaivers", "W-9s", "LoanDocs",
    "Closing", "ChangeOrders", "ProductData", "Warranties", "Manuals",
    "CertsOfOccupancy", "Photos", "DailyLogs", "Other",
];

const humanBytes = (b) => {
    if (b < 1024) return b + " B";
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
    return (b / 1024 / 1024).toFixed(1) + " MB";
};

/* ==================== FILES TAB ==================== */
export function FilesTab({ projectId }) {
    const [docs, setDocs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filterCat, setFilterCat] = useState("");
    const [filterTag, setFilterTag] = useState("");
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const load = async () => {
        const q = filterCat ? `?category=${encodeURIComponent(filterCat)}` : "";
        const { data } = await api.get(`/projects/${projectId}/documents${q}`);
        setDocs(data.items || []);
        setCategories(data.categories || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, filterCat]);

    const upload = async (files, category = "Other") => {
        if (!files.length) return;
        setUploading(true);
        for (const f of files) {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("category", category);
            try {
                await api.post(`/projects/${projectId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            } catch (e) {
                toast.error(`Upload failed: ${f.name}`);
            }
        }
        setUploading(false);
        toast.success(`${files.length} file(s) uploaded`);
        load();
    };

    const filteredDocs = useMemo(() => {
        return docs.filter((d) => !filterTag || (d.tags || []).includes(filterTag));
    }, [docs, filterTag]);

    return (
        <div className="space-y-4" data-testid="tab-files-content">
            <div className="flex flex-wrap items-center gap-3">
                <div>
                    <label className="text-xs text-muted-foreground mr-2">Category:</label>
                    <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="border border-border bg-background px-3 py-2 text-sm" data-testid="files-category-filter">
                        <option value="">All</option>
                        {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="relative">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input placeholder="tag filter" value={filterTag} onChange={(e) => setFilterTag(e.target.value)} className="border border-border bg-background pl-8 pr-3 py-2 text-sm" data-testid="files-tag-filter" />
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files), filterCat || "Other")} data-testid="files-upload-input" />
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-navy text-arch-white px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50" data-testid="files-upload-btn">
                        <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
                    </button>
                </div>
            </div>

            {/* Drag drop area */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(Array.from(e.dataTransfer.files), filterCat || "Other"); }}
                className={`border-2 border-dashed p-4 text-center text-xs ${dragOver ? "border-navy bg-navy/5" : "border-border text-muted-foreground"}`}
                data-testid="files-drop-zone"
            >
                Drag files here to upload{filterCat ? ` to category "${filterCat}"` : " (Other category by default)"}
            </div>

            {/* Grid */}
            {filteredDocs.length === 0 ? (
                <div className="border border-border p-10 text-center text-sm text-muted-foreground" data-testid="files-empty">
                    <FileText size={28} className="mx-auto mb-3" />
                    No documents yet. Drop files above to get started.
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="files-grid">
                    {filteredDocs.map((d) => <DocumentCard key={d.id} doc={d} onOpen={() => setSelectedDoc(d)} onDeleted={load} />)}
                </div>
            )}

            {selectedDoc && <DocumentPreview docId={selectedDoc.id} onClose={() => setSelectedDoc(null)} onChanged={load} />}
        </div>
    );
}

function DocumentCard({ doc, onOpen, onDeleted }) {
    const approvalColor = doc.approval_status === "approved" ? "text-emerald-500" :
        doc.approval_status === "rejected" ? "text-destructive" : "text-muted-foreground";
    return (
        <button onClick={onOpen} data-testid={`document-card-${doc.id}`}
            className="text-left border border-border p-4 hover:shadow-[0_10px_30px_-15px_rgba(15,39,71,0.35)] transition-shadow bg-card">
            <div className="flex items-start gap-3">
                <div className="shrink-0 h-10 w-10 border border-border flex items-center justify-center"><FileText size={16} /></div>
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate" title={doc.filename}>{doc.filename}</div>
                    <div className="mt-1 text-[11px] text-muted-foreground">{doc.category} · {humanBytes(doc.size_bytes)} · v{doc.version_number}</div>
                </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px]">
                <span className={`inline-flex items-center gap-1 ${approvalColor}`}>
                    {doc.approval_status === "approved" ? <CheckCircle2 size={11} /> : doc.approval_status === "rejected" ? <XCircle size={11} /> : <Clock size={11} />}
                    {doc.approval_status.replace(/_/g, " ")}
                </span>
                {doc.expiration_date && <span className="text-muted-foreground">Exp: {doc.expiration_date.slice(0, 10)}</span>}
            </div>
        </button>
    );
}

function DocumentPreview({ docId, onClose, onChanged }) {
    const [doc, setDoc] = useState(null);
    const load = async () => {
        const { data } = await api.get(`/documents/${docId}`);
        setDoc(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [docId]);
    if (!doc) return null;

    const downloadUrl = `${API_BASE}/documents/${docId}/download${doc.signed_download.split("/download")[1]}`;

    const approve = async (status) => {
        await api.post(`/documents/${docId}/approve`, { approval_status: status });
        toast.success(`Marked ${status}`);
        load(); onChanged?.();
    };
    const del = async () => {
        if (!window.confirm(`Delete "${doc.filename}" and all versions?`)) return;
        await api.delete(`/documents/${docId}`);
        toast.success("Deleted");
        onChanged?.(); onClose();
    };
    const copyShareLink = () => {
        navigator.clipboard.writeText(downloadUrl);
        toast.success("Share link copied (5-min signed URL)");
    };

    const isImage = (doc.mime_type || "").startsWith("image/");
    const isPdf = doc.mime_type === "application/pdf";

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="document-preview">
            <div className="bg-background border border-border w-full max-w-4xl my-6" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="font-medium text-sm">{doc.filename}</div>
                        <div className="text-[11px] text-muted-foreground">{doc.category} · {humanBytes(doc.size_bytes)} · v{doc.version_number}</div>
                    </div>
                    <button onClick={onClose} data-testid="preview-close"><X size={16} /></button>
                </div>
                <div className="p-5 grid md:grid-cols-3 gap-5">
                    <div className="md:col-span-2 border border-border min-h-[300px] flex items-center justify-center bg-secondary/20">
                        {isImage ? (
                            <img src={downloadUrl} alt={doc.filename} className="max-w-full max-h-[500px]" />
                        ) : isPdf ? (
                            <iframe src={downloadUrl} title={doc.filename} className="w-full h-[500px]" />
                        ) : (
                            <div className="text-center text-sm text-muted-foreground p-8">
                                <FileText size={40} className="mx-auto mb-3" />
                                Preview not available for this file type.
                                <div className="mt-3"><a href={downloadUrl} className="underline">Download to view</a></div>
                            </div>
                        )}
                    </div>
                    <div className="space-y-3 text-xs">
                        <div><div className="overline">Uploaded</div><div>{(doc.uploaded_at || "").slice(0, 19).replace("T", " ")}</div></div>
                        <div><div className="overline">Approval</div><div>{doc.approval_status}</div></div>
                        {doc.tags?.length > 0 && <div><div className="overline">Tags</div><div>{doc.tags.join(", ")}</div></div>}
                        <div><div className="overline">Versions ({doc.versions.length})</div>
                            <ul className="mt-1 space-y-0.5">
                                {doc.versions.map((v) => <li key={v.id}>v{v.version_number} — {(v.uploaded_at || "").slice(0, 10)}</li>)}
                            </ul>
                        </div>
                        <div className="pt-3 border-t border-border space-y-2">
                            <a href={downloadUrl} download className="w-full inline-flex items-center justify-center gap-2 border border-border px-3 py-1.5 text-xs hover:bg-secondary" data-testid="doc-download-btn"><Download size={12} /> Download</a>
                            <button onClick={copyShareLink} className="w-full inline-flex items-center justify-center gap-2 border border-border px-3 py-1.5 text-xs hover:bg-secondary" data-testid="doc-share-btn"><ShieldCheck size={12} /> Copy share link (5 min)</button>
                            {doc.approval_status !== "approved" && (
                                <button onClick={() => approve("approved")} className="w-full bg-emerald-500 text-white px-3 py-1.5 text-xs hover:opacity-90" data-testid="doc-approve-btn">Approve</button>
                            )}
                            {doc.approval_status !== "rejected" && (
                                <button onClick={() => approve("rejected")} className="w-full border border-destructive text-destructive px-3 py-1.5 text-xs hover:bg-destructive/10" data-testid="doc-reject-btn">Reject</button>
                            )}
                            <button onClick={del} className="w-full inline-flex items-center justify-center gap-2 text-destructive px-3 py-1.5 text-xs" data-testid="doc-delete-btn"><Trash2 size={12} /> Delete</button>
                        </div>
                        <div className="pt-2 border-t border-border text-[10px] text-muted-foreground italic">
                            E-signature: Integration Placeholder — DocuSign Coming
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ==================== PERMITS TAB ==================== */
export function PermitsTab({ projectId }) {
    const [permits, setPermits] = useState([]);
    const [types, setTypes] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [jurisdictions, setJurisdictions] = useState([]);
    const [creating, setCreating] = useState(false);
    const [selectedPermit, setSelectedPermit] = useState(null);

    const load = async () => {
        const [{ data: p }, { data: t }, { data: j }] = await Promise.all([
            api.get(`/projects/${projectId}/permits`),
            api.get(`/permit-types`),
            api.get(`/jurisdictions?limit=25`),
        ]);
        setPermits(p.items || []);
        setTypes(t.types || []); setStatuses(t.statuses || []);
        setJurisdictions(j.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    return (
        <div className="space-y-4" data-testid="tab-permits-content">
            <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{permits.length} permits</span>
                <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-navy text-arch-white px-3 py-1.5 text-xs hover:opacity-90" data-testid="add-permit-btn">
                    <Plus size={12} /> New permit
                </button>
            </div>
            <div className="border border-border" data-testid="permits-table">
                <div className="grid px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "1.5fr 1fr 1.5fr 1fr 1fr 1fr" }}>
                    <div>Type</div><div>Permit #</div><div>Jurisdiction</div><div>Status</div><div>Applied</div><div>Expires</div>
                </div>
                {permits.map((p) => {
                    const juris = jurisdictions.find((j) => j.id === p.jurisdiction_id);
                    const expWarn = p.expiration_date ? expirationWarning(p.expiration_date) : null;
                    return (
                        <button key={p.id} onClick={() => setSelectedPermit(p)} data-testid={`permit-row-${p.id}`}
                            className="w-full grid px-4 py-3 border-b border-border last:border-b-0 text-sm text-left hover:bg-secondary/40 items-center"
                            style={{ gridTemplateColumns: "1.5fr 1fr 1.5fr 1fr 1fr 1fr" }}>
                            <div className="font-medium">{p.permit_type}</div>
                            <div className="text-xs text-muted-foreground">{p.permit_number || "—"}</div>
                            <div className="text-xs">{juris ? `${juris.city}, ${juris.state}` : "—"}</div>
                            <StatusPill status={p.review_status} />
                            <div className="text-xs">{p.application_date?.slice(0, 10) || "—"}</div>
                            <div className="text-xs">{p.expiration_date?.slice(0, 10) || "—"} {expWarn && <ExpBadge kind={expWarn} />}</div>
                        </button>
                    );
                })}
                {permits.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No permits yet.</div>}
            </div>
            {creating && <PermitForm projectId={projectId} types={types} statuses={statuses} jurisdictions={jurisdictions} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
            {selectedPermit && <PermitDetail permit={selectedPermit} statuses={statuses} onClose={() => setSelectedPermit(null)} onChanged={load} />}
        </div>
    );
}

function StatusPill({ status }) {
    const colors = {
        approved: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
        issued: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
        in_review: "bg-steel/15 text-steel border-steel/30",
        comments_returned: "bg-amber-500/15 text-amber-500 border-amber-500/30",
        expired: "bg-destructive/15 text-destructive border-destructive/30",
    };
    return <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border ${colors[status] || "bg-secondary border-border text-muted-foreground"}`}>{(status || "").replace(/_/g, " ")}</span>;
}

function expirationWarning(dateStr) {
    const days = (new Date(dateStr) - new Date()) / (1000 * 3600 * 24);
    if (days < 30) return "critical";
    if (days < 90) return "warning";
    return null;
}
function ExpBadge({ kind }) {
    const cls = kind === "critical" ? "text-destructive" : "text-amber-500";
    return <span className={`inline-flex items-center gap-1 ml-1 ${cls}`}><AlertTriangle size={10} /></span>;
}

function PermitForm({ projectId, types, statuses, jurisdictions, onClose, onSaved }) {
    const [form, setForm] = useState({
        permit_type: types[0] || "Building", permit_number: "", jurisdiction_id: "",
        application_date: "", review_status: "not_started", fees_amount: 0, expiration_date: "", notes: "",
    });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/projects/${projectId}/permits`, {
                ...form,
                application_date: form.application_date || undefined,
                expiration_date: form.expiration_date || undefined,
                jurisdiction_id: form.jurisdiction_id || undefined,
            });
            toast.success("Permit created");
            onSaved?.();
        } catch { toast.error("Failed"); }
    };
    return (
        <Modal onClose={onClose} title="New permit" testid="permit-form">
            <form onSubmit={submit} className="p-5 space-y-3">
                <Row>
                    <Field label="Type"><select value={form.permit_type} onChange={(e) => setForm({ ...form, permit_type: e.target.value })} className="input" data-testid="permit-form-type">{types.map((t) => <option key={t}>{t}</option>)}</select></Field>
                    <Field label="Permit #"><input value={form.permit_number} onChange={(e) => setForm({ ...form, permit_number: e.target.value })} className="input" data-testid="permit-form-number" /></Field>
                </Row>
                <Field label="Jurisdiction"><select value={form.jurisdiction_id} onChange={(e) => setForm({ ...form, jurisdiction_id: e.target.value })} className="input" data-testid="permit-form-juris"><option value="">— Select —</option>{jurisdictions.map((j) => <option key={j.id} value={j.id}>{j.city}, {j.state} ({j.authority_name})</option>)}</select></Field>
                <Row>
                    <Field label="Application date"><input type="date" value={form.application_date} onChange={(e) => setForm({ ...form, application_date: e.target.value })} className="input" data-testid="permit-form-app-date" /></Field>
                    <Field label="Status"><select value={form.review_status} onChange={(e) => setForm({ ...form, review_status: e.target.value })} className="input" data-testid="permit-form-status">{statuses.map((s) => <option key={s}>{s}</option>)}</select></Field>
                </Row>
                <Row>
                    <Field label="Fees $"><input type="number" value={form.fees_amount} onChange={(e) => setForm({ ...form, fees_amount: parseFloat(e.target.value) || 0 })} className="input" data-testid="permit-form-fees" /></Field>
                    <Field label="Expiration"><input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} className="input" data-testid="permit-form-exp" /></Field>
                </Row>
                <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" data-testid="permit-form-notes" /></Field>
                <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={onClose} className="border border-border px-3 py-1.5 text-sm">Cancel</button><button type="submit" className="bg-navy text-arch-white px-4 py-1.5 text-sm" data-testid="permit-form-save">Save</button></div>
            </form>
        </Modal>
    );
}

function PermitDetail({ permit: initial, statuses, onClose, onChanged }) {
    const [permit, setPermit] = useState(initial);
    const [comment, setComment] = useState("");
    const [resubNotes, setResubNotes] = useState("");
    const reload = async () => {
        const { data } = await api.get(`/permits/${permit.id}`);
        setPermit(data);
        onChanged?.();
    };

    const addComment = async () => {
        if (!comment.trim()) return;
        await api.post(`/permits/${permit.id}/comments`, { body: comment, from_jurisdiction: true });
        setComment(""); reload();
    };
    const resubmit = async () => {
        if (!resubNotes.trim()) return;
        await api.post(`/permits/${permit.id}/resubmit`, { resubmission_notes: resubNotes });
        setResubNotes(""); reload();
        toast.success("Resubmitted");
    };
    const updateStatus = async (s) => { await api.patch(`/permits/${permit.id}`, { review_status: s }); reload(); };
    const del = async () => {
        if (!window.confirm("Delete this permit?")) return;
        await api.delete(`/permits/${permit.id}`); onChanged?.(); onClose();
    };

    const timeline = [
        ...(permit.comments || []).map((c) => ({ ...c, kind: c.from_jurisdiction ? "AHJ comment" : "Team comment", ts: c.at })),
        ...(permit.resubmissions || []).map((r) => ({ ...r, kind: "Resubmission", body: r.notes, ts: r.at })),
    ].sort((a, b) => (a.ts || "").localeCompare(b.ts || ""));

    return (
        <Modal onClose={onClose} title={`${permit.permit_type} — ${permit.permit_number || "(no #)"}`} testid="permit-detail" wide>
            <div className="p-5 grid md:grid-cols-3 gap-5">
                <div className="md:col-span-2 space-y-4">
                    <div className="flex items-center gap-2">
                        <span className="overline">Status:</span>
                        <select value={permit.review_status} onChange={(e) => updateStatus(e.target.value)} className="input text-xs" data-testid="permit-detail-status">
                            {statuses.map((s) => <option key={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="overline mb-2">Timeline</div>
                        <ul className="divide-y divide-border border border-border" data-testid="permit-timeline">
                            {timeline.length === 0 && <li className="p-3 text-xs text-muted-foreground">No activity yet.</li>}
                            {timeline.map((e, i) => (
                                <li key={i} className="p-3 text-xs">
                                    <div className="text-muted-foreground">{(e.ts || "").slice(0, 19).replace("T", " ")} — <b>{e.kind}</b></div>
                                    <div className="mt-1">{e.body}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="overline mb-1">Add AHJ comment</div>
                            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} className="input" data-testid="permit-comment-input" />
                            <button onClick={addComment} className="mt-2 bg-navy text-arch-white text-xs px-3 py-1.5" data-testid="permit-comment-save"><MessageSquare size={12} className="inline mr-1" /> Log comment</button>
                        </div>
                        <div>
                            <div className="overline mb-1">Resubmit</div>
                            <textarea value={resubNotes} onChange={(e) => setResubNotes(e.target.value)} rows={2} className="input" data-testid="permit-resub-input" />
                            <button onClick={resubmit} className="mt-2 bg-navy text-arch-white text-xs px-3 py-1.5" data-testid="permit-resub-save"><RotateCw size={12} className="inline mr-1" /> Resubmit</button>
                        </div>
                    </div>
                </div>
                <div className="text-xs space-y-3">
                    <Meta label="Fees" value={`$${permit.fees_amount || 0} (paid $${permit.fees_paid || 0})`} />
                    <Meta label="Applied" value={permit.application_date?.slice(0, 10) || "—"} />
                    <Meta label="Expires" value={permit.expiration_date?.slice(0, 10) || "—"} />
                    <Meta label="Notes" value={permit.notes || "—"} />
                    <button onClick={del} className="w-full mt-3 text-destructive text-xs inline-flex items-center justify-center gap-1" data-testid="permit-delete-btn"><Trash2 size={12} /> Delete permit</button>
                </div>
            </div>
        </Modal>
    );
}

/* ==================== INSPECTIONS TAB ==================== */
export function InspectionsTab({ projectId }) {
    const [rows, setRows] = useState([]);
    const [types, setTypes] = useState([]);
    const [results, setResults] = useState([]);
    const [permits, setPermits] = useState([]);
    const [creating, setCreating] = useState(false);
    const [selected, setSelected] = useState(null);
    const [view, setView] = useState("kanban");

    const load = async () => {
        const [{ data: I }, { data: T }, { data: P }] = await Promise.all([
            api.get(`/projects/${projectId}/inspections`),
            api.get(`/inspection-types`),
            api.get(`/projects/${projectId}/permits`),
        ]);
        setRows(I.items || []); setTypes(T.types || []); setResults(T.results || []);
        setPermits(P.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

    return (
        <div className="space-y-4" data-testid="tab-inspections-content">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <button onClick={() => setView("kanban")} className={`text-xs px-3 py-1.5 border ${view === "kanban" ? "border-navy bg-secondary" : "border-border"}`} data-testid="inspections-view-kanban">Kanban</button>
                    <button onClick={() => setView("list")} className={`text-xs px-3 py-1.5 border ${view === "list" ? "border-navy bg-secondary" : "border-border"}`} data-testid="inspections-view-list">List</button>
                </div>
                <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-navy text-arch-white px-3 py-1.5 text-xs hover:opacity-90" data-testid="add-inspection-btn">
                    <Plus size={12} /> New inspection
                </button>
            </div>

            {view === "kanban" ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="inspections-kanban">
                    {["requested", "scheduled", "passed", "failed", "corrections_in_progress", "reinspection_scheduled", "closed"].map((r) => {
                        const items = rows.filter((x) => x.result === r);
                        if (!items.length && !["requested", "scheduled", "passed", "failed"].includes(r)) return null;
                        return (
                            <div key={r} className="border border-border" data-testid={`inspections-col-${r}`}>
                                <div className="px-3 py-2 border-b border-border bg-secondary/40 text-[10px] uppercase tracking-widest">{r.replace(/_/g, " ")} ({items.length})</div>
                                <div className="p-2 space-y-2 max-h-[500px] overflow-y-auto">
                                    {items.map((i) => (
                                        <button key={i.id} onClick={() => setSelected(i)} className="w-full text-left border border-border p-2 text-xs bg-background hover:bg-secondary/40" data-testid={`inspection-card-${i.id}`}>
                                            <div className="font-medium">{i.inspection_type}</div>
                                            <div className="text-muted-foreground text-[10px] mt-1">{i.scheduled_date?.slice(0, 10) || i.requested_date?.slice(0, 10) || "no date"}</div>
                                            {i.inspector_name && <div className="text-[10px] text-muted-foreground">{i.inspector_name}</div>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="border border-border" data-testid="inspections-list">
                    <div className="grid px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground" style={{ gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr" }}>
                        <div>Type</div><div>Scheduled</div><div>Inspector</div><div>Result</div><div>Corrections</div>
                    </div>
                    {rows.map((i) => (
                        <button key={i.id} onClick={() => setSelected(i)} data-testid={`inspection-row-${i.id}`}
                            className="w-full grid px-4 py-3 border-b border-border last:border-b-0 text-sm text-left hover:bg-secondary/40 items-center"
                            style={{ gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr" }}>
                            <div>{i.inspection_type}</div>
                            <div className="text-xs">{i.scheduled_date?.slice(0, 10) || "—"}</div>
                            <div className="text-xs">{i.inspector_name || "—"}</div>
                            <div className="text-xs capitalize">{i.result.replace(/_/g, " ")}</div>
                            <div className="text-xs">{(i.corrections || []).length}</div>
                        </button>
                    ))}
                    {rows.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No inspections.</div>}
                </div>
            )}

            {creating && <InspectionForm projectId={projectId} types={types} results={results} permits={permits} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
            {selected && <InspectionDetail inspection={selected} results={results} onClose={() => setSelected(null)} onChanged={load} />}
        </div>
    );
}

function InspectionForm({ projectId, types, results, permits, onClose, onSaved }) {
    const [form, setForm] = useState({
        inspection_type: types[0] || "Foundation", permit_id: "", requested_date: "",
        scheduled_date: "", inspector_name: "", result: "requested", notes: "",
    });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/projects/${projectId}/inspections`, {
                ...form,
                permit_id: form.permit_id || undefined,
                requested_date: form.requested_date || undefined,
                scheduled_date: form.scheduled_date || undefined,
            });
            toast.success("Inspection created");
            onSaved?.();
        } catch { toast.error("Failed"); }
    };
    return (
        <Modal onClose={onClose} title="New inspection" testid="inspection-form">
            <form onSubmit={submit} className="p-5 space-y-3">
                <Row>
                    <Field label="Type"><select value={form.inspection_type} onChange={(e) => setForm({ ...form, inspection_type: e.target.value })} className="input" data-testid="inspection-form-type">{types.map((t) => <option key={t}>{t}</option>)}</select></Field>
                    <Field label="Permit"><select value={form.permit_id} onChange={(e) => setForm({ ...form, permit_id: e.target.value })} className="input" data-testid="inspection-form-permit"><option value="">— None —</option>{permits.map((p) => <option key={p.id} value={p.id}>{p.permit_type} {p.permit_number}</option>)}</select></Field>
                </Row>
                <Row>
                    <Field label="Requested"><input type="date" value={form.requested_date} onChange={(e) => setForm({ ...form, requested_date: e.target.value })} className="input" data-testid="inspection-form-req" /></Field>
                    <Field label="Scheduled"><input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="input" data-testid="inspection-form-sched" /></Field>
                </Row>
                <Row>
                    <Field label="Inspector"><input value={form.inspector_name} onChange={(e) => setForm({ ...form, inspector_name: e.target.value })} className="input" data-testid="inspection-form-inspector" /></Field>
                    <Field label="Result"><select value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} className="input" data-testid="inspection-form-result">{results.map((r) => <option key={r}>{r}</option>)}</select></Field>
                </Row>
                <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" data-testid="inspection-form-notes" /></Field>
                <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={onClose} className="border border-border px-3 py-1.5 text-sm">Cancel</button><button type="submit" className="bg-navy text-arch-white px-4 py-1.5 text-sm" data-testid="inspection-form-save">Save</button></div>
            </form>
        </Modal>
    );
}

function InspectionDetail({ inspection: initial, results, onClose, onChanged }) {
    const [insp, setInsp] = useState(initial);
    const [correction, setCorrection] = useState({ item: "", responsible_role: "" });
    const reload = async () => {
        const { data } = await api.get(`/inspections/${insp.id}`);
        setInsp(data); onChanged?.();
    };
    const updateResult = async (r) => { await api.patch(`/inspections/${insp.id}`, { result: r }); reload(); };
    const addCorrection = async () => {
        if (!correction.item.trim()) return;
        await api.post(`/inspections/${insp.id}/corrections`, correction);
        setCorrection({ item: "", responsible_role: "" }); reload();
    };
    const del = async () => {
        if (!window.confirm("Delete this inspection?")) return;
        await api.delete(`/inspections/${insp.id}`);
        onChanged?.(); onClose();
    };

    return (
        <Modal onClose={onClose} title={`${insp.inspection_type} inspection`} testid="inspection-detail" wide>
            <div className="p-5 space-y-4">
                <div className="grid md:grid-cols-4 gap-3">
                    <Meta label="Scheduled" value={insp.scheduled_date?.slice(0, 10) || "—"} />
                    <Meta label="Inspector" value={insp.inspector_name || "—"} />
                    <Meta label="Result">
                        <select value={insp.result} onChange={(e) => updateResult(e.target.value)} className="input text-xs" data-testid="inspection-detail-result">
                            {results.map((r) => <option key={r}>{r}</option>)}
                        </select>
                    </Meta>
                    <Meta label="Corrections" value={String((insp.corrections || []).length)} />
                </div>
                <div>
                    <div className="overline mb-2">Corrections</div>
                    <ul className="divide-y divide-border border border-border" data-testid="inspection-corrections">
                        {(insp.corrections || []).length === 0 && <li className="p-3 text-xs text-muted-foreground">No corrections.</li>}
                        {(insp.corrections || []).map((c) => (
                            <li key={c.id} className="p-3 text-xs">
                                <div><b>{c.item}</b> {c.resolved && <span className="text-emerald-500 ml-2">✓ resolved</span>}</div>
                                <div className="text-muted-foreground mt-1">{c.responsible_role || "—"} · {(c.at || "").slice(0, 10)}</div>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-3 flex gap-2 items-end">
                        <div className="flex-1">
                            <label className="overline block mb-1">Add correction item</label>
                            <input value={correction.item} onChange={(e) => setCorrection({ ...correction, item: e.target.value })} className="input" data-testid="correction-item" />
                        </div>
                        <div>
                            <label className="overline block mb-1">Responsible role</label>
                            <input value={correction.responsible_role} onChange={(e) => setCorrection({ ...correction, responsible_role: e.target.value })} className="input w-40" data-testid="correction-role" />
                        </div>
                        <button onClick={addCorrection} className="bg-navy text-arch-white px-3 py-1.5 text-xs" data-testid="add-correction-btn"><Plus size={12} className="inline mr-1" /> Add</button>
                    </div>
                </div>
                {insp.notes && <div className="text-xs"><div className="overline">Notes</div>{insp.notes}</div>}
                <button onClick={del} className="text-destructive text-xs inline-flex items-center gap-1" data-testid="inspection-delete-btn"><Trash2 size={12} /> Delete inspection</button>
            </div>
        </Modal>
    );
}

/* ==================== SHARED PRIMITIVES ==================== */
function Modal({ children, onClose, title, testid, wide = false }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
            <div className={`bg-background border border-border w-full ${wide ? "max-w-4xl" : "max-w-xl"} my-6`} onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="font-display text-xl italic">{title}</div>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}
function Row({ children }) { return <div className="grid grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children }) { return <div><label className="overline block mb-1">{label}</label>{children}</div>; }
function Meta({ label, value, children }) { return <div><div className="overline">{label}</div><div className="mt-1 text-sm">{children || value || "—"}</div></div>; }
